import React, { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNotifications } from '@/hooks/useNotifications';
import { useNotificationManager } from '@/contexts/NotificationManager';
import { NotificationCenterPanel } from '@/components/notifications';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Lottie from 'lottie-react';
import notificationBellAnimation from '@/assets/notification-bell-white.json';

interface NotificationDropdownProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

export default function NotificationDropdownWithPanel({ isOpen, onToggle }: NotificationDropdownProps) {
  const { isSignedIn } = useUser();
  const { unreadCount } = useNotifications();
  const { isConnected } = useNotificationManager();
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Sync with external isOpen prop if provided
  useEffect(() => {
    if (typeof isOpen === 'boolean') {
      setIsPanelOpen(isOpen);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsPanelOpen(false);
    onToggle?.();
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPanelOpen) handleClose();
    };
    if (isPanelOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isPanelOpen]);

  if (!isSignedIn) return null;

  return (
    <DropdownMenu open={isPanelOpen} onOpenChange={setIsPanelOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`
            relative p-1.5 rounded-lg transition-all duration-300 group overflow-visible
            ${unreadCount > 0 
              ? 'hover:bg-white/15 shadow-lg' 
              : 'hover:bg-white/10'
            }
          `}
          title="Notifications"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <Lottie 
              animationData={notificationBellAnimation}
              loop={true}
              autoplay={true}
              className="w-5 h-5 transition-all duration-300 group-hover:scale-110"
              style={{
                filter: 'brightness(0.95)',
                opacity: 0.9
              }}
            />
          </div>
          
          {/* Unread Count Badge - Top Right (Apple-style) */}
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[8px] font-bold min-w-[0.75rem] h-[0.75rem] rounded-full flex items-center justify-center px-0.5 shadow-md border border-white/30">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          
          
          {/* Connection Status Indicator - Top Right with padding */}
          {unreadCount === 0 && (
            <div 
              className={`
                absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full 
                ${isConnected ? 'bg-green-400' : 'bg-yellow-400'} 
                opacity-80 animate-pulse
              `}
              title={isConnected ? 'Connected' : 'Reconnecting...'}
            />
          )}
        </button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        sideOffset={8}
        className="p-0 border-0 bg-transparent shadow-none"
        style={{ width: '420px' }}
      >
        <NotificationCenterPanel onClose={handleClose} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
